import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';

// OPTIMIZATION START - CPU fallback support
let poseVision = null;
let poseLandmarker = null;
let currentPoseDelegate = null;
let poseDelegateOverride = null;
let poseFallbackUsed = false;
// OPTIMIZATION END

// KNOX DATA CLEAR DETECTION: localStorage marker
const POSE_INIT_MARKER_KEY = 'mediapipe_pose_init_marker';

let lastPoseTime = 0;
let repetitionCount = 0;
let previousWristHeight = 0;
let hasSpawnedApple = false;
let isInDownPosition = true;
let isMovingUp = false;
let onRepetitionCallback = null;
const THRESHOLD_HEAD = 0.15;
const THRESHOLD_SHOULDER = 0.15;
const MOVEMENT_THRESHOLD = 0.02;
const COOLDOWN = 1000;

// OPTIMIZATION: Ensure vision is loaded (with caching)
async function ensurePoseVision() {
    if (poseVision) return poseVision;

    poseVision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
    );
    return poseVision;
}

// OPTIMIZATION: Initialize with optional delegate specification
export async function initPoseDetection(delegate = 'GPU') {
    // KNOX DATA CLEAR DETECTION: Check for external data clearing
    try {
        const marker = localStorage.getItem(POSE_INIT_MARKER_KEY);
        if (!marker && poseLandmarker) {
            console.warn('[PoseDetection] 🚨 Knox data clear detected, forcing cleanup of corrupted poseLandmarker');
            try {
                poseLandmarker.close();
            } catch(e) {
                console.warn('[PoseDetection] poseLandmarker.close() failed:', e.message);
            }
            poseLandmarker = null;
        }
    } catch(e) {
        // localStorage not available (private mode), skip check
    }

    const vision = await ensurePoseVision();

    // Allow override for testing
    const actualDelegate = poseDelegateOverride || delegate;
    currentPoseDelegate = actualDelegate;

    console.log(`[PoseDetection] Initializing with ${actualDelegate} delegate`);

    poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
            delegate: actualDelegate
        },
        runningMode: "VIDEO",
        numPoses: 1
    });

    // Set marker after successful init
    try {
        localStorage.setItem(POSE_INIT_MARKER_KEY, Date.now().toString());
    } catch(e) {
        // localStorage not available (private mode), skip
    }
}

// OPTIMIZATION: Switch delegate at runtime (GPU → CPU fallback)
async function switchPoseDelegate(newDelegate) {
    console.log(`[PoseDetection] Switching delegate from ${currentPoseDelegate} to ${newDelegate}`);

    const vision = await ensurePoseVision();
    currentPoseDelegate = newDelegate;
    poseFallbackUsed = true;

    poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
            delegate: newDelegate
        },
        runningMode: "VIDEO",
        numPoses: 1
    });
}

export async function detectPose(video, timestamp) {
    if (!poseLandmarker) return null;

    // Check if video has valid dimensions before processing
    if (!video || video.videoWidth === 0 || video.videoHeight === 0 ||
        video.readyState < video.HAVE_METADATA) {
        return null;
    }

    try {
        const results = await poseLandmarker.detectForVideo(video, timestamp);
        return analyzePoseForHandRaise(results);
    } catch (error) {
        console.warn("Pose detection error:", error.message);

        // OPTIMIZATION: Try CPU fallback if GPU fails
        if (currentPoseDelegate === 'GPU' && !poseFallbackUsed) {
            console.log('[PoseDetection] GPU failed, attempting CPU fallback...');
            try {
                await switchPoseDelegate('CPU');
                const retryResults = await poseLandmarker.detectForVideo(video, timestamp);
                return analyzePoseForHandRaise(retryResults);
            } catch (fallbackError) {
                console.warn("Pose detection CPU fallback failed:", fallbackError.message);
            }
        }

        return null;
    }
}

export function setOnRepetitionCallback(callback) {
    onRepetitionCallback = callback;
}

function analyzePoseForHandRaise(results) {
    if (!results?.landmarks?.[0]) return false;

    const currentTime = Date.now();
    if (currentTime - lastPoseTime < COOLDOWN) return false;

    const pose = results.landmarks[0];
    const nose = pose[0];
    const leftWrist = pose[15];
    const rightWrist = pose[16];
    const leftShoulder = pose[11];
    const rightShoulder = pose[12];

    const activeWrist = leftWrist.y < rightWrist.y ? leftWrist : rightWrist;
    const activeShoulder = leftWrist.y < rightWrist.y ? leftShoulder : rightShoulder;

    const heightDifference = previousWristHeight - activeWrist.y;
    isMovingUp = heightDifference < -MOVEMENT_THRESHOLD;
    previousWristHeight = activeWrist.y;

    const isAboveHead = activeWrist.y < (nose.y - THRESHOLD_HEAD) &&
        Math.abs(activeWrist.x - activeShoulder.x) < 0.15 &&
        activeWrist.z < activeShoulder.z;

    const isBelowShoulder = activeWrist.y > (activeShoulder.y + THRESHOLD_SHOULDER);

    if (isBelowShoulder && !isInDownPosition) {
        isInDownPosition = true;
        hasSpawnedApple = false;
        console.log('Reset: Hands returned to down position');
        return false;
    }

    if (isAboveHead && isInDownPosition && !hasSpawnedApple && isMovingUp) {
        isInDownPosition = false;
        hasSpawnedApple = true;
        lastPoseTime = currentTime;
        repetitionCount++;
        console.log(`COMPLETE REP ${repetitionCount} - Apple spawned`);
        if (onRepetitionCallback) {
            onRepetitionCallback(repetitionCount);
        }
        return true;
    }

    return false;
}

export function getRepetitionCount() {
    return repetitionCount;
}

// CRITICAL: Cleanup function to release MediaPipe PoseLandmarker WASM memory
export function cleanupPoseDetection() {
    console.log('[PoseDetection] Cleaning up pose landmarker');

    // Close pose landmarker to release WASM memory
    if (poseLandmarker) {
        try {
            poseLandmarker.close();
            poseLandmarker = null;
            console.log('[PoseDetection] PoseLandmarker closed successfully');
        } catch (error) {
            console.warn('[PoseDetection] Error closing PoseLandmarker:', error);
        }
    }

    // Reset state variables
    repetitionCount = 0;
    previousWristHeight = 0;
    hasSpawnedApple = false;
    isInDownPosition = true;
    isMovingUp = false;
    lastPoseTime = 0;
    onRepetitionCallback = null;
    currentPoseDelegate = null;
    poseFallbackUsed = false;

    // Clear init marker
    try {
        localStorage.removeItem(POSE_INIT_MARKER_KEY);
    } catch(e) {
        // localStorage not available
    }

    console.log('[PoseDetection] Cleanup complete');
}

export default initPoseDetection;